import React from 'react';
import { useSubscription } from '../context/SubscriptionContext';
import { useLocale } from '../context/LocaleContext';
import { Clock, Sparkles, X } from 'lucide-react';

/**
 * 🎯 Trial Banner - Subtle, non-intrusive trial countdown
 * 
 * Behavior:
 * - Shows during active trial only
 * - Displays days remaining
 * - CTA to upgrade (optional, not pushy)
 * - Can be dismissed (reappears on page refresh if ≤2 days)
 */
const TrialBanner = () => {
  const { trialStatus, showExpiringPaywall, subscriptionStatus } = useSubscription();
  const { t, formatPrice, pricing } = useLocale();
  const [dismissed, setDismissed] = React.useState(false);

  // Don't show if:
  // - Not trialing
  // - Already has paid subscription
  // - Banner dismissed (unless expiring soon)
  if (!trialStatus.isTrialing || subscriptionStatus.plan !== 'trial') {
    return null;
  }

  if (dismissed && !trialStatus.isExpiringSoon) {
    return null;
  }

  const { daysRemaining, isExpiringSoon } = trialStatus;

  // Different styles based on urgency
  const bannerStyle = isExpiringSoon
    ? 'bg-gradient-to-r from-orange-500/10 to-red-500/10 border-orange-200'
    : 'bg-gradient-to-r from-teal-500/10 to-cyan-500/10 border-teal-200';

  const textColor = isExpiringSoon ? 'text-orange-700' : 'text-teal-700';
  const buttonStyle = isExpiringSoon
    ? 'bg-orange-500 hover:bg-orange-600'
    : 'bg-teal-500 hover:bg-teal-600';

  return (
    <div className={`${bannerStyle} border-b px-4 py-2.5`}>
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
        {/* Left - Trial info */}
        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-1.5 ${textColor}`}>
            <Clock className="w-4 h-4" />
            <span className="text-sm font-medium">
              {daysRemaining === 1 
                ? (t('subscription.lastDayTrial') || 'Last day of trial')
                : daysRemaining === 0
                  ? (t('subscription.trialEndsToday') || 'Trial ends today')
                  : `${daysRemaining} ${t('subscription.daysLeftTrial') || 'days left in trial'}`}
            </span>
          </div>
          
          {pricing && pricing.monthly_price && (
            <span className="text-xs text-gray-500 hidden sm:inline">
              {t('subscription.then') || 'Then'} {formatPrice ? formatPrice(pricing.monthly_price) : `$${pricing.monthly_price}`}/{t('subscription.mo') || 'mo'}
            </span>
          )}
        </div>

        {/* Right - CTA and dismiss */}
        <div className="flex items-center gap-2">
          {isExpiringSoon && (
            <button
              onClick={showExpiringPaywall}
              className={`${buttonStyle} text-white text-xs px-3 py-1.5 rounded-full font-medium flex items-center gap-1.5 transition-colors`}
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span>Upgrade Now</span>
            </button>
          )}
          
          {!isExpiringSoon && (
            <button
              onClick={() => setDismissed(true)}
              className="text-gray-400 hover:text-gray-600 p-1"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrialBanner;
